// RelationshipPicker · universal, entity-agnostic multi/single-select
// with inline search, quick-create, and inline view/edit.
// ---------------------------------------------------------------------
// Product Owner directive (Feb 2026) · Free-text lists ended their life
// with the Traveler migration. Every module that needs to reference a
// canonical entity — Travelers, Suppliers, Ships, Hotels, Loyalty
// programs — now uses THIS component.
//
// Adding an entity type requires a single new descriptor in
// `entityRegistry.js`; RelationshipPicker itself never changes.
//
// Props
// ─────
//   entityType    "traveler" | "supplier" | "ship" | …
//   value         Canonical selection:
//                   - multi=true  → { ids: string[], names?: string[] }   (recommended)
//                                   OR a plain string[] of ids for convenience
//                   - multi=false → id string | null
//   legacyNames   (multi only) Free-text names to seed the picker when
//                 canonical ids haven't been captured yet. Kept for
//                 back-compat with pre-migration documents.
//   onChange(sel) - multi=true  → { ids, names, records }
//                 - multi=false → { id, name, record }
//   multi         Default true.
//   allowCreate   Default true — hides the "Create X 'foo'" affordance.
//   allowEdit     Default true — shows the pencil icon on chips.
//   allowView     Default true — clicking the label on a chip opens view.
//   placeholder   Optional. Default derives from descriptor.
//   testId        Optional. Root data-testid.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Loader2, Pencil, Eye, ChevronDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { getDescriptor, matchesQuery, norm } from "./entityRegistry";
import RelationshipEditDrawer from "./RelationshipEditDrawer";

// ── Value normalizers ──────────────────────────────────────────────
function normalizeMultiValue(value, legacyNames) {
  if (Array.isArray(value)) return { ids: value.filter(Boolean), names: legacyNames || [] };
  const ids = value?.ids || value?.traveler_ids || [];
  const names = value?.names || legacyNames || [];
  return { ids: ids.filter(Boolean), names: names || [] };
}

export default function RelationshipPicker({
  entityType,
  value,
  legacyNames = [],
  onChange,
  multi = true,
  allowCreate = true,
  allowEdit = true,
  allowView = true,
  placeholder,
  testId,
}) {
  const descriptor = useMemo(() => getDescriptor(entityType), [entityType]);
  const rootTestId = testId || `cc-rel-${entityType}`;

  const [directory, setDirectory] = useState([]);   // Array<record>
  const [dirLoaded, setDirLoaded] = useState(false);
  const [dirError, setDirError] = useState(null);
  const [selected, setSelected] = useState([]);      // [{ id, name, resolved, record? }]
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [drawerRec, setDrawerRec] = useState(null);
  const [drawerMode, setDrawerMode] = useState("edit");
  const inputRef = useRef(null);

  const Icon = descriptor.icon;

  // Directory load ─────────────────────────────────────────────────
  useEffect(() => {
    if (descriptor.scaffold && !directory.length) {
      // Stub descriptor — endpoint may not exist yet. Try, but fail
      // silently and show a coming-soon banner.
    }
    let cancelled = false;
    setDirLoaded(false);
    setDirError(null);
    api.get(descriptor.listPath)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.[descriptor.listKey] || [];
        setDirectory(Array.isArray(list) ? list : []);
        setDirLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setDirectory([]);
        setDirLoaded(true);
        setDirError(descriptor.scaffold
          ? `${descriptor.entityLabelPlural} directory is not available yet.`
          : formatApiError(e));
      });
    return () => { cancelled = true; };
  }, [descriptor]);

  // Seed selection ─────────────────────────────────────────────────
  useEffect(() => {
    if (!dirLoaded) return;
    const byId = new Map(directory.map((r) => [r.id, r]));
    const byName = new Map(directory.map((r) => [norm(descriptor.labelOf(r)), r]));

    if (multi) {
      const { ids, names } = normalizeMultiValue(value, legacyNames);
      const seeded = [];
      for (const id of ids) {
        const rec = byId.get(id);
        seeded.push(rec
          ? { id: rec.id, name: descriptor.labelOf(rec), resolved: true, record: rec }
          : { id, name: id, resolved: false, record: null });
      }
      if (seeded.length === 0 && Array.isArray(names)) {
        for (const nm of names) {
          const rec = byName.get(norm(nm));
          seeded.push(rec
            ? { id: rec.id, name: descriptor.labelOf(rec), resolved: true, record: rec }
            : { id: null, name: nm, resolved: false, record: null });
        }
      }
      setSelected(seeded);
    } else {
      const id = typeof value === "string" ? value : value?.id || null;
      if (!id) return setSelected([]);
      const rec = byId.get(id);
      setSelected(rec
        ? [{ id: rec.id, name: descriptor.labelOf(rec), resolved: true, record: rec }]
        : [{ id, name: id, resolved: false, record: null }]);
    }
  }, [dirLoaded, directory, JSON.stringify(value), JSON.stringify(legacyNames), multi, descriptor]);

  // Selection commit ──────────────────────────────────────────────
  const commit = (next) => {
    setSelected(next);
    if (multi) {
      onChange && onChange({
        ids: next.filter((s) => s.id).map((s) => s.id),
        names: next.map((s) => s.name),
        records: next.map((s) => s.record).filter(Boolean),
      });
    } else {
      const s = next[0];
      onChange && onChange(s
        ? { id: s.id, name: s.name, record: s.record }
        : { id: null, name: null, record: null });
    }
  };

  const isSelected = (id) => selected.some((s) => s.id === id);

  const results = useMemo(() => {
    const n = norm(query);
    if (!n && !open) return [];
    return directory
      .filter((r) => !isSelected(r.id) && matchesQuery(descriptor, r, query))
      .slice(0, 12);
  }, [query, directory, selected, descriptor, open]);

  const addRecord = (rec) => {
    const chip = { id: rec.id, name: descriptor.labelOf(rec), resolved: true, record: rec };
    commit(multi ? [...selected, chip] : [chip]);
    setQuery("");
    if (multi) inputRef.current?.focus();
    else setOpen(false);
  };

  const removeAt = (idx) => commit(selected.filter((_, i) => i !== idx));

  const quickCreate = async () => {
    if (!allowCreate || !descriptor.quickCreatable || !descriptor.createPath) return;
    const raw = query.trim();
    if (!raw) return;
    setCreating(true);
    try {
      const payload = descriptor.toCreatePayload(raw);
      const { data } = await api.post(descriptor.createPath, payload);
      // Some create endpoints wrap the record, most return it directly.
      const rec = data?.id ? data : data?.[descriptor.listKey]?.[0] || data;
      setDirectory((d) => [...d, rec]);
      addRecord(rec);
      toast.success(`${descriptor.entityLabel} "${descriptor.labelOf(rec)}" created and selected`);
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(`Unable to create ${descriptor.entityLabel.toLowerCase()}: ${msg}`);
    } finally {
      setCreating(false);
    }
  };

  const openDrawer = (rec, mode) => {
    if (!rec) return;
    setDrawerMode(mode);
    setDrawerRec(rec);
  };

  const onDrawerSaved = (updated) => {
    // Refresh directory + reflect the change on the selected chip.
    setDirectory((d) => d.map((r) => r.id === updated.id ? updated : r));
    setSelected((sel) => sel.map((s) => s.id === updated.id
      ? { ...s, name: descriptor.labelOf(updated), record: updated, resolved: true }
      : s));
    // Push canonical change upstream so parent forms stay in sync
    // (e.g. display names change downstream).
    if (multi) {
      onChange && onChange({
        ids: selected.filter((s) => s.id).map((s) => s.id),
        names: selected.map((s) => s.id === updated.id ? descriptor.labelOf(updated) : s.name),
        records: selected.map((s) => s.id === updated.id ? updated : s.record).filter(Boolean),
      });
    }
  };

  const showMenu = open && (query.trim() || results.length > 0 || (allowCreate && descriptor.quickCreatable));
  const placeholderText = placeholder
    || (selected.length === 0
      ? `Add ${descriptor.entityLabel.toLowerCase()}…`
      : (multi ? "" : "Change…"));

  return (
    <div data-testid={rootTestId} className="space-y-1.5 relative">
      {/* Chip row */}
      <div
        className="flex flex-wrap gap-1.5 items-center rounded-lg border border-slate-200 bg-white p-2 min-h-[42px] focus-within:ring-2 focus-within:ring-[#8B5CF6]/20 focus-within:border-[#DDD6FE] transition-shadow"
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((s, i) => (
          <Chip
            key={`${s.id || "u"}-${i}`}
            entry={s}
            descriptor={descriptor}
            testIdPrefix={rootTestId}
            index={i}
            allowEdit={allowEdit && s.resolved && !!descriptor.updatePath}
            allowView={allowView && s.resolved}
            onRemove={() => removeAt(i)}
            onView={() => openDrawer(s.record, "view")}
            onEdit={() => openDrawer(s.record, "edit")}
          />
        ))}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (results[0]) addRecord(results[0]);
              else if (query.trim() && allowCreate && descriptor.quickCreatable) quickCreate();
            }
            if (e.key === "Backspace" && !query && selected.length && multi) {
              removeAt(selected.length - 1);
            }
          }}
          placeholder={placeholderText}
          disabled={!multi && selected.length > 0}
          className="flex-1 min-w-[120px] text-[13px] outline-none bg-transparent disabled:cursor-not-allowed"
          data-testid={`${rootTestId}-input`}
        />

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); inputRef.current?.focus(); }}
          aria-label="Toggle picker"
          className="text-[#94A3B8] hover:text-[#0F172A] p-0.5"
          data-testid={`${rootTestId}-toggle`}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {dirError && !descriptor.scaffold && (
        <div className="text-[11px] text-red-700" data-testid={`${rootTestId}-dir-error`}>{dirError}</div>
      )}

      {/* Persistent dropdown */}
      {showMenu && (
        <div
          data-testid={`${rootTestId}-menu`}
          className="absolute z-40 left-0 right-0 mt-1 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden max-h-72 flex flex-col"
        >
          <div className="flex-1 overflow-y-auto">
            {!dirLoaded ? (
              <div className="px-3 py-6 text-center text-[12px] text-[#64748B] inline-flex items-center gap-1.5 w-full justify-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {descriptor.entityLabelPlural.toLowerCase()}…
              </div>
            ) : descriptor.scaffold && directory.length === 0 ? (
              <div className="px-3 py-3 text-[12px] text-[#64748B]">
                <div className="font-semibold text-[#0F172A]">{descriptor.entityLabelPlural} not available yet</div>
                <div>The universal picker is wired — the API is arriving in the next release.</div>
              </div>
            ) : results.length > 0 ? (
              <ul>
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      data-testid={`${rootTestId}-option-${r.id}`}
                      onMouseDown={(e) => { e.preventDefault(); addRecord(r); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-[13px] flex items-center gap-2"
                    >
                      <Icon className={`w-3.5 h-3.5 ${descriptor.themeTint.split(" ").find((c) => c.startsWith("text-")) || ""}`} />
                      <span className="font-medium text-[#0F172A]">{descriptor.labelOf(r)}</span>
                      {descriptor.subtitleOf(r) && (
                        <span className="text-[11px] text-[#64748B] ml-auto">{descriptor.subtitleOf(r)}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-2 text-[12px] text-[#64748B]">
                {query.trim() ? `No ${descriptor.entityLabelPlural.toLowerCase()} match "${query.trim()}".` : `Start typing to search ${descriptor.entityLabelPlural.toLowerCase()}.`}
              </div>
            )}
          </div>

          {/* Quick create */}
          {allowCreate && descriptor.quickCreatable && descriptor.createPath && query.trim() && (
            <button
              type="button"
              data-testid={`${rootTestId}-create`}
              disabled={creating}
              onMouseDown={(e) => { e.preventDefault(); quickCreate(); }}
              className="w-full flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-[12px] font-semibold text-[#8B5CF6] hover:bg-slate-50 disabled:opacity-60"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create {descriptor.entityLabel} “{query.trim()}”
            </button>
          )}
        </div>
      )}

      <RelationshipEditDrawer
        open={!!drawerRec}
        descriptor={descriptor}
        record={drawerRec}
        mode={drawerMode}
        testIdPrefix={`${rootTestId}-drawer`}
        onClose={() => setDrawerRec(null)}
        onSaved={(updated) => { onDrawerSaved(updated); }}
      />
    </div>
  );
}

// ── Chip ────────────────────────────────────────────────────────────
function Chip({ entry, descriptor, testIdPrefix, index, allowEdit, allowView, onRemove, onView, onEdit }) {
  const Icon = descriptor.icon;
  const tint = entry.resolved ? descriptor.themeTint : "bg-amber-100 text-amber-900";
  const isInteractive = allowView && entry.record;
  return (
    <span
      data-testid={`${testIdPrefix}-chip-${index}`}
      title={entry.resolved
        ? `Linked to canonical ${descriptor.entityLabel}`
        : `Legacy free-text ${descriptor.entityLabel.toLowerCase()} — not yet linked`}
      className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-1 text-[12px] font-semibold ${tint}`}
    >
      <Icon className="w-3 h-3" />
      {isInteractive ? (
        <button
          type="button"
          data-testid={`${testIdPrefix}-chip-${index}-view`}
          onClick={(e) => { e.stopPropagation(); onView(); }}
          className="hover:underline underline-offset-2"
        >
          {entry.name}
        </button>
      ) : (
        <span>{entry.name}</span>
      )}
      {allowEdit && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`${testIdPrefix}-chip-${index}-edit`}
          aria-label={`Edit ${entry.name}`}
          className="ml-1 opacity-70 hover:opacity-100 hover:bg-white/40 rounded-full p-0.5"
        >
          <Pencil className="w-2.5 h-2.5" />
        </button>
      )}
      {allowView && !allowEdit && entry.record && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onView(); }}
          data-testid={`${testIdPrefix}-chip-${index}-view-btn`}
          aria-label={`View ${entry.name}`}
          className="ml-1 opacity-70 hover:opacity-100 hover:bg-white/40 rounded-full p-0.5"
        >
          <Eye className="w-2.5 h-2.5" />
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`${testIdPrefix}-remove-${index}`}
        aria-label={`Remove ${entry.name}`}
        className="ml-0.5 hover:bg-white/40 rounded-full p-0.5"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
